import { useEffect, useState } from 'react'
import { Film, Video } from 'lucide-react'
import { cn } from '@/lib/utils'
import { recordingPoster } from './api'

/**
 * A frame from the recording, or the icon.
 *
 * The media server captures a JPEG every thirty seconds while a room is being
 * recorded, and the last one becomes the poster. Before that existed, the Live
 * Now and Archive boards were rows of identical black tiles with an icon in the
 * middle — PollSphere's own boards still are, because nothing there ever captures
 * a frame.
 *
 * ## Why this is a component and not an `<img src>`
 *
 * The object is private and served from MinIO behind a presigned URL, so the
 * source is a round trip, not a string we can compose. That has two consequences
 * this component exists to contain:
 *
 * 1. **It must never block the card.** A board of twelve cards is twelve presign
 *    requests; each resolves on its own and the icon stays until one does. A
 *    failure is silent and permanent for that card — a poster is decoration, and
 *    an error strip over a thumbnail would be worse than no thumbnail.
 * 2. **The URL expires.** An hour, per the backend. A board left open overnight
 *    would show broken images, so `onError` falls back to the icon rather than to
 *    the browser's broken-image glyph.
 */
export function CoveragePoster({
  recordingId, hasPoster, kind = 'video', className,
}: {
  /** Null while a feed has no recording yet — the icon is the whole answer then. */
  recordingId: string | null
  /** `posterFileId != null`. Skips the request entirely when there is nothing to fetch. */
  hasPoster: boolean
  kind?: 'video' | 'film'
  className?: string
}) {
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    setUrl(null)
    setFailed(false)
    if (!recordingId || !hasPoster) return
    let cancelled = false
    recordingPoster(recordingId)
      .then((p) => { if (!cancelled && p.url) setUrl(p.url) })
      // Silent. See the header: a poster is decoration and a card without one is
      // a working card.
      .catch(() => { if (!cancelled) setFailed(true) })
    return () => { cancelled = true }
  }, [recordingId, hasPoster])

  const Icon = kind === 'film' ? Film : Video

  return (
    <div className={cn('relative overflow-hidden bg-black', className)}>
      {url && !failed ? (
        <img
          src={url}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="grid h-full place-items-center">
          <Icon className="h-7 w-7 text-white/30" />
        </div>
      )}
    </div>
  )
}
